import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { TextPlugin } from 'gsap/TextPlugin';
import Lenis from '@studio-freight/lenis';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, EffectFade, Navigation, Pagination, Parallax } from 'swiper/modules';
import { useGSAP } from '@gsap/react';
import Navbar from '../../../components/Navbar';
import Footer from '../../../components/Footer';
import { Link } from 'react-router-dom';

// Import styles
import 'swiper/css';
import 'swiper/css/effect-fade';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import 'swiper/css/parallax';

// Register GSAP plugins
gsap.registerPlugin(ScrollTrigger, TextPlugin);

const Catalog = () => {
  // State management
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeHeroSlide, setActiveHeroSlide] = useState(0);
  const [scrollY, setScrollY] = useState(0);
  const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
  const [cursorText, setCursorText] = useState('');
  const [cursorVariant, setCursorVariant] = useState('default');
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedSize, setSelectedSize] = useState(null);
  const [activeItem, setActiveItem] = useState(null);
  const [swipeDirection, setSwipeDirection] = useState(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [revealedItems, setRevealedItems] = useState({});

  // Smooth scroll value for parallax effects
  const smoothScrollY = useMotionValue(0);
  const smoothY = useSpring(smoothScrollY, { damping: 20, stiffness: 100 });

  // Refs
  const mainRef = useRef(null);
  const gridRef = useRef(null);
  const heroHeadingRef = useRef(null);
  const cursorRef = useRef(null);
  const swipersRef = useRef([]);
  const heroVideoRef = useRef(null);
  const catalogTitleRef = useRef(null);
  
  // Hero slide content
  const heroSlidesRef = useRef([
    {
      image: "https://images.unsplash.com/photo-1529139574466-a303027c1d8b?q=80&w=1374&auto=format&fit=crop",
      video: "https://player.vimeo.com/progressive_redirect/playback/735282624/rendition/720p/file.mp4?loc=external&signature=e860d7ba975023a088e4056f685dc5433d44f533965795be7c48a8f5c6837020",
      title: "Spring/Summer",
      accent: "Collection",
      desc: "Discover our latest designs crafted for the modern individual who embraces both style and purpose."
    },
    {
      image: "https://images.unsplash.com/photo-1445205170230-053b83016050?q=80&w=1471&auto=format&fit=crop",
      video: "https://player.vimeo.com/progressive_redirect/playback/735281838/rendition/720p/file.mp4?loc=external&signature=d5ec327c7c0a13ca72219494784d5ef2783465ce3bec24d75101e29361d1bfbf",
      title: "Architectural",
      accent: "Elegance",
      desc: "Bold silhouettes with refined details that create a statement of effortless sophistication."
    },
    {
      image: "https://images.unsplash.com/photo-1490481651871-ab68de25d43d?q=80&w=1470&auto=format&fit=crop",
      video: "https://player.vimeo.com/progressive_redirect/playback/735273590/rendition/720p/file.mp4?loc=external&signature=b7661e6f9497191781587bc54bd7461a9c5abcf72a8710cfcdad6a50350ada40",
      title: "Monochrome",
      accent: "Essentials",
      desc: "Timeless pieces designed with precision and crafted with the finest sustainable materials."
    }
  ]);

  // Categories
  const categories = [
    { id: 'all', label: 'All' },
    { id: 'clothing', label: 'Apparel' },
    { id: 'accessories', label: 'Accessories' },
    { id: 'shoes', label: 'Footwear' },
    { id: 'new', label: 'New Arrivals' },
    { id: 'premium', label: 'Premium' }
  ];

  // Products
  const products = [
    {
      id: 1,
      name: 'Monochrome Elegance Sneakers',
      category: 'shoes',
      price: 329.99,
      image: 'https://images.unsplash.com/photo-1600269452121-4f2416e55c28?q=80&w=1374&auto=format&fit=crop',
      secondaryImage: 'https://images.unsplash.com/photo-1543163521-1bf539c55dd2?q=80&w=1480&auto=format&fit=crop',
      description: 'Minimalist design meets premium comfort in these versatile sneakers crafted from Italian leather with a unique sole construction for ultimate flexibility.',
      tags: ['new', 'trending'],
      sizes: ['38', '39', '40', '41', '42', '43'],
      colors: ['#000000', '#FFFFFF', '#D3D3D3']
    },
    {
      id: 2,
      name: 'Cobalt Technical Jacket',
      category: 'clothing',
      price: 589.50,
      image: 'https://images.unsplash.com/photo-1591047139829-d91aecb6caea?q=80&w=1336&auto=format&fit=crop',
      secondaryImage: 'https://images.unsplash.com/photo-1578681994506-b8f463449011?q=80&w=1635&auto=format&fit=crop',
      description: 'Weather-resistant technical jacket with ergonomic paneling and hidden ventilation systems. Crafted using recycled performance fabrics.',
      tags: ['exclusive', 'premium'],
      sizes: ['S', 'M', 'L', 'XL'],
      colors: ['#1338BE', '#000000', '#454545']
    },
    {
      id: 3,
      name: 'Phoenix Combat Boots',
      category: 'shoes',
      price: 520.00,
      image: 'https://images.unsplash.com/photo-1621996659490-3275307d9ed4?q=80&w=1480&auto=format&fit=crop',
      secondaryImage: 'https://images.unsplash.com/photo-1605812860427-4024433a70fd?q=80&w=1335&auto=format&fit=crop',
      description: 'Urban-inspired boots handcrafted from premium calfskin leather with reinforced stitching and custom hardware for durability and distinctive style.',
      tags: ['bestseller', 'premium'],
      sizes: ['39', '40', '41', '42', '43', '44', '45'],
      colors: ['#3A3A3A', '#614B35', '#000000']
    },
    {
      id: 4,
      name: 'Structured Wool Overcoat',
      category: 'clothing',
      price: 810.00,
      image: 'https://images.unsplash.com/photo-1539533018447-63fcce2678e3?q=80&w=1374&auto=format&fit=crop',
      secondaryImage: 'https://images.unsplash.com/photo-1544022613-e87ca75a784a?q=80&w=1374&auto=format&fit=crop',
      description: 'Contemporary silhouette with tailored structure crafted from Italian merino wool. Features custom-developed matte buttons and silk-blend lining.',
      tags: ['premium', 'new'],
      sizes: ['S', 'M', 'L', 'XL', 'XXL'],
      colors: ['#373737', '#614B35', '#5C5C5C']
    },
    {
      id: 5,
      name: 'Platform Neon Sneakers',
      category: 'shoes',
      price: 445.00,
      image: 'https://images.unsplash.com/photo-1560769629-975ec94e6a86?q=80&w=1287&auto=format&fit=crop',
      secondaryImage: 'https://images.unsplash.com/photo-1579446745462-adb2be9ceda3?q=80&w=1480&auto=format&fit=crop',
      description: 'Statement footwear with architectural platform and vibrant accents. Features advanced cushioning technology and breathable construction.',
      tags: ['new', 'limited'],
      sizes: ['36', '37', '38', '39', '40', '41'],
      colors: ['#F8FF1C', '#FF43A5', '#FFFFFF']
    },
    {
      id: 6,
      name: 'Asymmetric Cashmere Cardigan',
      category: 'clothing',
      price: 675.00,
      image: 'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?q=80&w=1349&auto=format&fit=crop',
      secondaryImage: 'https://images.unsplash.com/photo-1503341733017-1901578f9f1e?q=80&w=1287&auto=format&fit=crop',
      description: 'Architectural knitwear design with premium grade-A cashmere sourced from Mongolia. Features a draped silhouette and distinctive asymmetric closure.',
      tags: ['premium', 'sustainable'],
      sizes: ['XS', 'S', 'M', 'L', 'XL'],
      colors: ['#C1BDAE', '#000000', '#726F69']
    },
    {
      id: 7,
      name: 'Sculpted Leather Tote',
      category: 'accessories',
      price: 890.00,
      image: 'https://images.unsplash.com/photo-1590874103328-eac38a683ce7?q=80&w=1438&auto=format&fit=crop',
      secondaryImage: 'https://images.unsplash.com/photo-1606522754091-a3bbf9ad4cb3?q=80&w=1374&auto=format&fit=crop',
      description: 'Vegetable-tanned leather tote handcrafted in small batches with minimal seams for a sculptural silhouette. Features custom brass hardware and suede interior.',
      tags: ['exclusive', 'sustainable'],
      sizes: ['One Size'],
      colors: ['#614B35', '#000000', '#8B572A']
    },
    {
      id: 8,
      name: 'Technical Performance Shorts',
      category: 'clothing',
      price: 278.00,
      image: 'https://images.unsplash.com/photo-1562183241-840b8af0721c?q=80&w=1335&auto=format&fit=crop',
      secondaryImage: 'https://images.unsplash.com/photo-1619603364904-c0498317e145?q=80&w=1470&auto=format&fit=crop',
      description: 'Performance shorts with four-way stretch fabric developed exclusively with Swiss textile innovators. Features laser-cut ventilation and bonded seams.',
      tags: ['new', 'sport'],
      sizes: ['XS', 'S', 'M', 'L', 'XL'],
      colors: ['#000000', '#2D2D2D', '#4A4A4A']
    },
    {
      id: 9,
      name: 'Geometric Silk Scarf',
      category: 'accessories',
      price: 315.00,
      image: 'https://images.unsplash.com/photo-1584273143981-41c073dfe8f8?q=80&w=1287&auto=format&fit=crop',
      secondaryImage: 'https://images.unsplash.com/photo-1522621032211-ac0031dfbddc?q=80&w=1333&auto=format&fit=crop',
      description: 'Hand-printed silk twill scarf featuring original geometric artwork. Produced in limited quantities with natural dyes and hand-rolled edges.',
      tags: ['premium', 'accessories'],
      sizes: ['One Size'],
      colors: ['#E0A67B', '#FFDBB8', '#C2815E']
    }
  ];

  // Filter products based on active filter
  const filteredProducts = activeFilter === 'all' 
    ? products 
    : products.filter(product => product.category === activeFilter || product.tags.includes(activeFilter));

  // Initialize smooth scroll
  useEffect(() => {
    // Create smooth scroll instance
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    // Connect lenis to requestAnimationFrame
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    
    requestAnimationFrame(raf);

    // Update scroll position for animations
    lenis.on('scroll', ({ scroll }) => {
      setScrollY(scroll);
      smoothScrollY.set(scroll);
    });

    // Cleanup on unmount
    return () => {
      lenis.destroy();
    };
  }, []);

  // Advanced page initialization with sequential reveal
  useEffect(() => {
    // Initial loading sequence
    const loadingTimeline = gsap.timeline({ 
      onComplete: () => setIsLoaded(true) 
    });

    // Preload all images
    const preloadImages = () => {
      const imagesToPreload = [
        ...heroSlidesRef.current.map(slide => slide.image),
        ...products.map(product => [product.image, product.secondaryImage]).flat()
      ].filter(Boolean);
      
      return Promise.all(imagesToPreload.map((src) => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = src;
          img.onload = resolve;
          img.onerror = resolve; // Continue even if an image fails
        });
      }));
    };

    // Execute preload and then animation sequence
    preloadImages().then(() => {
      // Fade out loader
      loadingTimeline.to('.page-loader', { 
        opacity: 0,
        duration: 0.8,
        ease: "power2.inOut",
        onComplete: () => {
          document.querySelector('.page-loader')?.classList.add('hidden');
        }
      });

      // Reveal the page content
      loadingTimeline.from('.catalog-hero', { 
        opacity: 0, 
        duration: 0.8,
        ease: "power2.out" 
      }, "-=0.4");

      // Initialize all section animations
      initSectionAnimations();
    });

    // Initialize GSAP scroll animations
    function initSectionAnimations() {
      // Parallax hero image
      gsap.to('.hero-image', {
        yPercent: 30,
        ease: "none",
        scrollTrigger: {
          trigger: '.catalog-hero',
          start: "top top",
          end: "bottom top",
          scrub: true,
        }
      });
      
      // Stagger in product grid items
      if (gridRef.current) {
        ScrollTrigger.batch(".product-card", {
          interval: 0.1,
          batchMax: 3,
          onEnter: batch => {
            gsap.to(batch, {
              opacity: 1,
              y: 0,
              stagger: 0.15,
              overwrite: true,
              ease: "power3.out",
            });
          },
          start: "top bottom-=100px",
          once: true
        });
      }

      // Collection features section parallax
      gsap.to('.collection-image', {
        yPercent: 20,
        ease: "none",
        scrollTrigger: {
          trigger: '.collection-section',
          start: "top bottom",
          end: "bottom top",
          scrub: true
        }
      });
    }

    // Clean up scroll triggers on unmount
    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  // Custom cursor effect
  useEffect(() => {
    const handleMouseMove = (event) => {
      setCursorPosition({ x: event.clientX, y: event.clientY });
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
    };
  }, []);


  // Handle product item detail view
  const openItemDetail = (item) => {
    setActiveItem(item);
    setSelectedSize(null);
    document.body.style.overflow = 'hidden';
    setCursorVariant('default');
  };
  
  const closeItemDetail = () => {
    setActiveItem(null);
    document.body.style.overflow = '';
  };
  
  const handleSizeSelect = (size) => {
    setSelectedSize(size);
  };
  
  const handleColorSelect = (color) => {
    // Placeholder for color selection in the future
    console.log(`Color selected: ${color}`);
  };
  
  // Handle cursor variants
  const handleCursorEnter = (text, variant = 'hover') => {
    setCursorText(text);
    setCursorVariant(variant);
  };
  
  const handleCursorLeave = () => {
    setCursorText('');
    setCursorVariant('default');
  };
  
  // Cursor animation variants
  const cursorVariants = {
    default: {
      x: cursorPosition.x - 16,
      y: cursorPosition.y - 16,
      height: 32,
      width: 32,
      backgroundColor: "rgba(255, 255, 255, 0.2)",
      border: "1px solid rgba(255, 255, 255, 0.5)",
      fontSize: "0px",
    },
    hover: {
      x: cursorPosition.x - 40,
      y: cursorPosition.y - 40,
      height: 80,
      width: 80,
      backgroundColor: "rgba(224, 166, 123, 0.2)",
      border: "1px solid #E0A67B",
      fontSize: "16px",
      color: "#E0A67B",
    },
    view: {
      x: cursorPosition.x - 60,
      y: cursorPosition.y - 60,
      height: 120,
      width: 120,
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      border: "none",
      fontSize: "18px",
      color: "white",
    }
  };

  // Animation variants for grid items
  const staggerContainer = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  
  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: { 
      opacity: 1, 
      y: 0,
      transition: {
        type: "tween",
        ease: "easeOut",
        duration: 0.8
      }
    }
  };

  // Handle next slide in hero section with direction animation
  const handleNextSlide = () => {
    setSwipeDirection('left');
    setActiveHeroSlide((prevActive) => {
      const nextSlide = (prevActive + 1) % heroSlidesRef.current.length;
      return nextSlide;
    });
  };

  // Handle previous slide in hero section with direction animation
  const handlePrevSlide = () => {
    setSwipeDirection('right');
    setActiveHeroSlide((prevActive) => {
      const prevSlide = prevActive === 0 ? heroSlidesRef.current.length - 1 : prevActive - 1;
      return prevSlide;
    });
  };

  // Callback for product visible on screen tracking
  const handleProductInView = (productId, inView) => {
    if (inView) {
      setRevealedItems(prev => ({ ...prev, [productId]: true }));
    }
  };

  // Create a product observer with intersection observer
  const ProductObserver = ({ product, children }) => {
    const [ref, inView] = useInView({
      threshold: 0.2,
      triggerOnce: true
    });
    
    useEffect(() => {
      if (inView) {
        handleProductInView(product.id, true);
      }
    }, [inView, product.id]);
    
    return (
      <div ref={ref}>
        {children}
      </div>
    );
  };

  // Collection features data
  const collectionFeatures = [
    {
      title: 'Sustainable Materials',
      description: 'Responsibly sourced fabrics and materials that minimize environmental impact without compromising on quality or durability.'
    },
    {
      title: 'Engineered for Movement',
      description: 'Strategic paneling and articulation to enhance range of motion with ergonomically designed silhouettes for all-day comfort.'
    },
    {
      title: 'Modular Design Approach',
      description: 'Versatile, layerable pieces that can be styled multiple ways to maximize your wardrobe efficiency throughout changing seasons.'
    },
    {
      title: 'Architectural Details',
      description: 'Precision crafted elements that create visual interest through structure, proportion, and thoughtful construction methods.'
    }
  ];

  return (
    <div className="catalog-container bg-white text-black min-h-screen overflow-hidden">
      {/* Page Loader */}
      <div className="page-loader fixed inset-0 bg-white z-[100] flex items-center justify-center">
        <div className="relative flex flex-col items-center">
          <div className="w-24 h-24 border-t-4 border-[#E0A67B] border-solid rounded-full animate-spin mb-8"></div>
          <div className="text-[#E0A67B] tracking-[0.3em] uppercase text-sm">Loading Collection</div>
        </div>
      </div>

      {/* Header */}
      <Navbar />
      
      {/* Custom Cursor */}
      <motion.div
        ref={cursorRef}
        className="custom-cursor fixed left-0 top-0 pointer-events-none mix-blend-difference z-50 flex items-center justify-center rounded-full overflow-hidden hidden md:flex"
        variants={cursorVariants}
        animate={cursorVariant}
        transition={{ type: "spring", stiffness: 1000, damping: 50, mass: 0.5 }}
      >
        <span className="text-center font-light tracking-widest">{cursorText}</span>
      </motion.div>
      
      {/* Hero Section with Video Background */}
      <section className="catalog-hero relative h-[95vh] overflow-hidden">
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={activeHeroSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: [0.33, 1, 0.68, 1] }}
            className="absolute inset-0 z-0"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent z-10"></div>
            
            {/* Video Background */}
            <motion.div 
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 10, ease: "easeOut" }}
              className="absolute inset-0 hero-image"
            >
              <div className="hidden sm:block absolute inset-0">
                <video
                  ref={heroVideoRef}
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                  src={heroSlidesRef.current[activeHeroSlide].video}
                >
                  <source src={heroSlidesRef.current[activeHeroSlide].video} type="video/mp4" />
                </video>
              </div>
              <img 
                src={heroSlidesRef.current[activeHeroSlide].image}
                alt="Hero image" 
                className="sm:hidden w-full h-full object-cover object-center"
              />
            </motion.div>
          </motion.div>
        </AnimatePresence>
        
        <div className="container mx-auto px-6 h-full flex items-center relative z-10">
          <div className="max-w-xl text-white">
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={activeHeroSlide}
                initial={{ opacity: 0, y: swipeDirection === 'left' ? 60 : -60 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: swipeDirection === 'left' ? -60 : 60 }}
                transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
              >
                <span className="text-sm uppercase tracking-[0.3em] block mb-6 opacity-80">
                  {activeHeroSlide === 0 ? "New Season" : activeHeroSlide === 1 ? "Limited Edition" : "Exclusive Collection"}
                </span>
                <h1 ref={catalogTitleRef} className="catalog-title text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6 tracking-tighter">
                  {heroSlidesRef.current[activeHeroSlide].title} <br/>
                  <span className="text-[#E0A67B]">{heroSlidesRef.current[activeHeroSlide].accent}</span>
                </h1>
                <p className="catalog-subtitle text-lg md:text-xl mb-10 max-w-md leading-relaxed opacity-90">
                  {heroSlidesRef.current[activeHeroSlide].desc}
                </p>
                <div className="flex flex-wrap gap-6">
                  <motion.button 
                    className="px-8 py-3 bg-white text-black font-medium hover:bg-[#E0A67B] hover:text-white transition-all duration-500"
                    onMouseEnter={() => handleCursorEnter('Shop', 'hover')}
                    onMouseLeave={handleCursorLeave}
                    whileTap={{ scale: 0.98 }}
                  >
                    Shop Collection
                  </motion.button>
                  <motion.button 
                    className="px-8 py-3 border border-white text-white font-medium hover:bg-white hover:text-black transition-all duration-500"
                    onMouseEnter={() => handleCursorEnter('Explore', 'hover')}
                    onMouseLeave={handleCursorLeave}
                    whileTap={{ scale: 0.98 }}
                  >
                    Explore Lookbook
                  </motion.button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        
        {/* Carousel Navigation Controls */}
        <div className="absolute bottom-16 right-16 flex items-center space-x-6 z-20">
          <motion.button 
            onClick={handlePrevSlide}
            className="w-12 h-12 border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all duration-300"
            onMouseEnter={() => handleCursorEnter('Prev', 'hover')}
            onMouseLeave={handleCursorLeave}
            whileTap={{ scale: 0.9 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
            </svg>
          </motion.button>
          <div className="text-white">
            <span className="text-3xl font-light">{String(activeHeroSlide + 1).padStart(2, '0')}</span>
            <span className="mx-2 text-white/50">—</span>
            <span className="text-white/50 text-lg">{String(heroSlidesRef.current.length).padStart(2, '0')}</span>
          </div>
          <motion.button 
            onClick={handleNextSlide}
            className="w-12 h-12 border border-white/30 flex items-center justify-center text-white hover:bg-white hover:text-black transition-all duration-300"
            onMouseEnter={() => handleCursorEnter('Next', 'hover')}
            onMouseLeave={handleCursorLeave}
            whileTap={{ scale: 0.9 }}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5l7 7-7 7" />
            </svg>
          </motion.button>
        </div>
        
        {/* Hero Collection Stats */}
        <div className="absolute bottom-16 left-16 flex items-center space-x-16 z-20">
          <div className="text-white text-center">
            <span className="block text-5xl font-light">45</span>
            <span className="text-xs uppercase tracking-[0.2em] text-white/70">New Items</span>
          </div>
          <div className="text-white text-center">
            <span className="block text-5xl font-light">12</span>
            <span className="text-xs uppercase tracking-[0.2em] text-white/70">Collections</span>
          </div>
        </div>
        
        {/* Scroll Indicator */}
        <motion.div 
          className="absolute bottom-10 left-1/2 transform -translate-x-1/2 flex flex-col items-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5, duration: 1 }}
        >
          <span className="text-white/70 text-xs uppercase tracking-[0.3em] mb-2">Scroll</span>
          <motion.div 
            className="w-[1px] h-10 bg-white/50"
            animate={{ 
              scaleY: [1, 0.3, 1],
              opacity: [0.5, 1, 0.5],
            }}
            transition={{
              duration: 2,
              ease: "easeInOut",
              repeat: Infinity,
              repeatType: "loop"
            }}
          ></motion.div>
        </motion.div>
      </section>
      
      {/* Seasonal Collection Highlight */}
      <section className="py-32 overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row gap-16 items-center">
            <motion.div 
              className="md:w-1/2 mb-10 md:mb-0"
              initial={{ opacity: 0, x: -80 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 1, ease: [0.25, 1, 0.5, 1] }}
            >
              <img 
                src="https://images.unsplash.com/photo-1525507119028-ed4c629a60a3?q=80&w=1335&auto=format&fit=crop"
                alt="Seasonal collection"
                className="w-full h-[600px] object-cover object-center"
              />
            </motion.div>
            
            <div className="md:w-1/2">
              <motion.div
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                <span className="text-[#E0A67B] uppercase tracking-[0.3em] block mb-4">Season Highlight</span>
                <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">The Modern <br/>Essentials</h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed max-w-lg">
                  Rediscover timeless staples reimagined with contemporary details and premium materials. These foundational pieces form the backbone of any sophisticated wardrobe.
                </p>
                
                <div className="grid grid-cols-2 gap-6 mb-10">
                  <div className="border-t pt-4 border-gray-200">
                    <h3 className="text-xl font-medium mb-2">Considered Design</h3>
                    <p className="text-gray-600">Thoughtfully crafted with intention behind every detail</p>
                  </div>
                  <div className="border-t pt-4 border-gray-200">
                    <h3 className="text-xl font-medium mb-2">Premium Materials</h3>
                    <p className="text-gray-600">Sourced globally from the finest sustainable suppliers</p>
                  </div>
                </div>
                
                <Link to="/catalog" className="inline-flex items-center text-[#E0A67B] font-medium group">
                  <span className="mr-2">Explore the Collection</span>
                  <span className="relative overflow-hidden inline-block w-8">
                    <motion.span 
                      className="inline-block"
                      initial={{ x: -20, opacity: 0 }}
                      animate={{ x: 0, opacity: 1 }}
                      transition={{ duration: 0.3, delay: 0.1 }}
                    >→</motion.span>
                  </span>
                </Link>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Main Collection Grid */}
      <main ref={mainRef} className="container mx-auto px-6 py-20">
        <header className="mb-16 flex flex-col md:flex-row justify-between items-start md:items-end">
          <div className="mb-8 md:mb-0">
            <motion.span 
              className="text-[#E0A67B] uppercase tracking-[0.3em] block mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8 }}
            >
              Curated Selection
            </motion.span>
            <motion.h2 
              className="text-4xl md:text-5xl font-bold tracking-tight"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              Our Collection
            </motion.h2>
          </div>
          
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <motion.button
                key={category.id}
                onClick={() => setActiveFilter(category.id)}
                className={`filter-item px-6 py-2.5 text-sm font-medium tracking-wide transition-all ${
                  activeFilter === category.id
                    ? 'bg-black text-white'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
                whileTap={{ scale: 0.98 }}
                onMouseEnter={() => handleCursorEnter(category.label, 'hover')}
                onMouseLeave={handleCursorLeave}
              >
                {category.label}
              </motion.button>
            ))}
          </div>
        </header>

        {/* Products Grid with More Stable Framer Motion Animations */}
        <div 
          ref={gridRef}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12"
        >
          {filteredProducts.map((product) => (
            <motion.div 
              key={product.id}
              className="product-card group cursor-pointer"
              onClick={() => openItemDetail(product)}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
              whileHover={{ y: -15 }}
              onMouseEnter={() => handleCursorEnter('View', 'view')}
              onMouseLeave={handleCursorLeave}
            >
              <div className="relative overflow-hidden aspect-[3/4]">
                <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/30 opacity-0 group-hover:opacity-100 transition-opacity duration-700 z-10"></div>
                
                {/* Product Tag Badges */}
                {product.tags.includes('new') && (
                  <div className="absolute top-4 left-4 z-10 px-3 py-1.5 bg-white text-xs uppercase tracking-wider font-medium">
                    New
                  </div>
                )}
                {product.tags.includes('premium') && (
                  <div className="absolute top-4 right-4 z-10 px-3 py-1.5 bg-[#E0A67B] text-white text-xs uppercase tracking-wider font-medium">
                    Premium
                  </div>
                )}
                
                <motion.div 
                  className="relative h-full w-full"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 1, ease: [0.25, 1, 0.5, 1] }}
                >
                  {/* Main Product Image */}
                  <img
                    src={product.image}
                    alt={product.name}
                    className="absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-1000 group-hover:opacity-0"
                  />
                  
                  {/* Secondary Product Image on Hover */}
                  <img
                    src={product.secondaryImage || product.image}
                    alt={product.name}
                    className="absolute inset-0 w-full h-full object-cover object-center opacity-0 group-hover:opacity-100 transition-opacity duration-1000"
                  />
                </motion.div>
                
                {/* Quick Shop Button */}
                <div className="absolute bottom-0 left-0 right-0 p-6 z-20 transform translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-in-out">
                  <button 
                    className="w-full py-3 bg-white hover:bg-[#E0A67B] hover:text-white text-black font-medium text-sm uppercase tracking-wider transition-colors duration-300"
                    onClick={(e) => {
                      e.stopPropagation();
                      openItemDetail(product);
                    }}
                  >
                    Quick Shop
                  </button>
                </div>
              </div>
              
              <div className="pt-6">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium">{product.name}</h3>
                    <p className="text-gray-500 capitalize mt-1">{product.category}</p>
                  </div>
                  <span className="text-lg font-medium">${product.price.toFixed(2)}</span>
                </div>
                
                {/* Color Options Preview */}
                <div className="mt-3 flex gap-2">
                  {product.colors?.map((color, i) => (
                    <div 
                      key={i} 
                      className="w-4 h-4 rounded-full border border-gray-200 cursor-pointer"
                      style={{ backgroundColor: color }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleColorSelect(color);
                      }}
                    ></div>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Load More Button */}
        <div className="mt-20 text-center">
          <motion.button
            className="px-10 py-4 border border-black bg-white text-black font-medium hover:bg-black hover:text-white transition-all duration-300"
            whileTap={{ scale: 0.98 }}
          >
            Load More
          </motion.button>
        </div>
      </main>
      
      {/* Trending Section with Parallax Effect */}
      <section className="py-32 bg-gray-50 overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="flex flex-col lg:flex-row gap-16 items-center">
            <motion.div 
              className="lg:w-1/3"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-[#E0A67B] uppercase tracking-[0.3em] block mb-4">What's Popular</span>
              <h2 className="text-4xl md:text-5xl font-bold mb-6 tracking-tight">Trending Now</h2>
              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Our most coveted pieces, selected by you. Discover what's making an impact in the fashion world through our meticulously curated trending collection.
              </p>
              <motion.button 
                className="px-8 py-3 border border-black text-black font-medium hover:bg-black hover:text-white transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onMouseEnter={() => handleCursorEnter('Discover', 'hover')}
                onMouseLeave={handleCursorLeave}
              >
                View All Trending
              </motion.button>
            </motion.div>
            
            <div className="lg:w-2/3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                {products.filter(p => p.tags.includes('trending')).slice(0, 2).map((product, idx) => (
                  <motion.div
                    key={product.id}
                    initial={{ opacity: 0, y: 50 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: idx * 0.2 }}
                    className="trending-item cursor-pointer overflow-hidden"
                    onClick={() => openItemDetail(product)}
                    onMouseEnter={() => handleCursorEnter('View', 'view')}
                    onMouseLeave={handleCursorLeave}
                  >
                    <motion.div 
                      className="relative overflow-hidden"
                      whileHover={{ scale: 1.03 }}
                      transition={{ duration: 0.7, ease: [0.25, 1, 0.5, 1] }}
                    >
                      <img 
                        src={product.image} 
                        alt={product.name}
                        className="w-full h-[600px] object-cover object-center"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                        <h3 className="text-2xl font-medium mb-2">{product.name}</h3>
                        <div className="flex justify-between items-center">
                          <span className="text-xl">${product.price.toFixed(2)}</span>
                          <motion.span 
                            className="uppercase text-sm tracking-[0.2em] flex items-center"
                            whileHover={{ x: 5 }}
                          >
                            Shop Now 
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                            </svg>
                          </motion.span>
                        </div>
                      </div>
                    </motion.div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Collection Feature Section with Parallax */}
      <section className="py-36 bg-white overflow-hidden collection-section">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -80 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.2, ease: [0.25, 1, 0.5, 1] }}
            >
              <div className="relative">
                <motion.div
                  className="h-[800px] overflow-hidden"
                >
                  <div className="absolute -bottom-12 -right-12 w-32 h-32 bg-[#E0A67B] z-10 hidden lg:block"></div>
                  <img 
                    src="https://images.unsplash.com/photo-1548123378-8c858b6979b6?q=80&w=1287&auto=format&fit=crop" 
                    alt="Collection feature" 
                    className="w-full h-full object-cover object-center collection-image"
                  />
                </motion.div>
                <div className="absolute -bottom-12 -left-12 w-24 h-24 bg-[#E0A67B] hidden lg:block"></div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 80 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.2, delay: 0.2, ease: [0.25, 1, 0.5, 1] }}
              className="max-w-md"
            >
              <span className="text-[#E0A67B] uppercase tracking-[0.3em] block mb-4">Latest Collection</span>
              <h2 className="text-4xl md:text-5xl font-bold mt-2 mb-8 tracking-tight">Architectural Minimalism</h2>
              <p className="text-lg text-gray-600 mb-10 leading-relaxed">
                Our latest collection explores the intersection of architectural precision and minimalist design philosophy. 
                Each piece is constructed with purpose, featuring clean lines and considered proportions for a refined aesthetic that transcends seasons.
              </p>
              
              <ul className="space-y-6 mb-12">
                {collectionFeatures.map((feature, idx) => (
                  <motion.li 
                    key={idx} 
                    className="flex items-start"
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.3 + (idx * 0.1) }}
                  >
                    <span className="w-8 h-8 rounded-full bg-[#E0A67B] flex items-center justify-center text-white mr-4 mt-0.5 flex-shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </span>
                    <div>
                      <h4 className="font-medium text-lg">{feature.title}</h4>
                      <p className="text-gray-500 mt-1">{feature.description}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>
              
              <motion.button 
                className="px-10 py-4 bg-black text-white font-medium hover:bg-[#E0A67B] transition-colors duration-500"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                onMouseEnter={() => handleCursorEnter('Explore', 'hover')}
                onMouseLeave={handleCursorLeave}
              >
                Explore Collection
              </motion.button>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Editorial Picks Section with Infinite Carousel */}
      <section className="py-32 bg-[#F8F3F1] overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <motion.span 
              className="text-[#E0A67B] uppercase tracking-[0.3em] block mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              Editor's Selection
            </motion.span>
            <motion.h2 
              className="text-4xl md:text-5xl font-bold"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              Editorial Picks
            </motion.h2>
          </div>
          
          {/* Premium editorial selection with Swiper */}
          <div className="relative">
            <Swiper
              modules={[Navigation, Pagination]}
              spaceBetween={30}
              slidesPerView={1}
              breakpoints={{
                640: { slidesPerView: 2 },
                1024: { slidesPerView: 3 }
              }}
              navigation
              pagination={{ clickable: true }}
              className="editorial-swiper !overflow-visible"
            >
              {products.filter(p => p.tags.includes('premium')).map((product) => (
                <SwiperSlide key={product.id} className="pb-12">
                  <motion.div 
                    className="editorial-item cursor-pointer"
                    onClick={() => openItemDetail(product)}
                    onMouseEnter={() => handleCursorEnter('View', 'view')}
                    onMouseLeave={handleCursorLeave}
                    whileHover={{ y: -15 }}
                  >
                    <div className="relative overflow-hidden mb-8">
                      <motion.div
                        whileHover={{ scale: 1.05 }}
                        transition={{ duration: 0.8, ease: [0.25, 1, 0.5, 1] }}
                      >
                        <img 
                          src={product.image} 
                          alt={product.name}
                          className="w-full h-[560px] object-cover object-center"
                        />
                      </motion.div>
                    </div>
                    <h3 className="text-2xl font-medium mb-3">{product.name}</h3>
                    <p className="text-gray-600 mb-6 line-clamp-2">{product.description}</p>
                    <motion.span 
                      className="text-[#E0A67B] font-medium inline-flex items-center"
                      whileHover={{ x: 5 }}
                    >
                      Shop Now
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 ml-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </motion.span>
                  </motion.div>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>
        </div>
      </section>
      
      {/* Design Philosophy Section with Split Layout */}
      <section className="py-32 bg-white overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-20">
            <div>
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <span className="text-[#E0A67B] uppercase tracking-[0.3em] block mb-4">Design Philosophy</span>
                <h2 className="text-4xl md:text-5xl font-bold mb-8 tracking-tight">Crafted With Purpose</h2>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Every piece in our collection is created with intention, from the initial concept through the final stitch. 
                  We believe in timeless design that transcends seasons and trends, creating garments that become more 
                  meaningful with each wear.
                </p>
                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                  Our materials are selected not only for their premium quality and feel, but also for their 
                  longevity and reduced environmental impact. This commitment to conscious creation defines 
                  every collection we release.
                </p>
                <blockquote className="border-l-4 border-[#E0A67B] pl-6 italic mb-10">
                  "We design for the individual who appreciates subtlety and substance over fleeting trends."
                </blockquote>
                <motion.button 
                  className="px-8 py-3 border border-black text-black font-medium hover:bg-black hover:text-white transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Our Approach
                </motion.button>
              </motion.div>
            </div>
            
            <div className="grid grid-cols-2 gap-6 h-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="h-full flex flex-col gap-6"
              >
                <div className="bg-[#F8F3F1] p-8 h-1/2 flex flex-col justify-center">
                  <h3 className="text-xl font-medium mb-3">Sustainable Sourcing</h3>
                  <p className="text-gray-600">Materials selected for minimal environmental impact without sacrificing quality</p>
                </div>
                <div className="relative overflow-hidden h-1/2">
                  <img 
                    src="https://images.unsplash.com/photo-1579220672220-77c1cb6a9b51?q=80&w=1287&auto=format&fit=crop"
                    alt="Design Detail" 
                    className="h-full w-full object-cover"
                  />
                </div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="h-full flex flex-col gap-6"
              >
                <div className="relative overflow-hidden h-2/3">
                  <img 
                    src="https://images.unsplash.com/photo-1619785292559-a15caa28bde4?q=80&w=1335&auto=format&fit=crop"
                    alt="Precision Craftsmanship" 
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="bg-[#F8F3F1] p-8 h-1/3 flex flex-col justify-center">
                  <h3 className="text-xl font-medium mb-3">Precision Craftsmanship</h3>
                  <p className="text-gray-600">Attention to detail in every stitch and seam</p>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Newsletter Section */}
      <section className="py-32 bg-black text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <motion.span 
              className="text-[#E0A67B] uppercase tracking-[0.3em] block mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              Stay Connected
            </motion.span>
            <motion.h2 
              className="text-4xl md:text-5xl font-bold mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
            >
              Join Our Community
            </motion.h2>
            <motion.p 
              className="text-lg text-gray-300 mb-12 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Subscribe to receive early access to new collections, exclusive content, and invitations to members-only events. Be part of our creative journey.
            </motion.p>
            
            <motion.form 
              className="flex max-w-lg mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <div className="flex flex-col sm:flex-row w-full">
                <input 
                  type="email" 
                  placeholder="Your email address" 
                  className="flex-grow px-6 py-4 bg-transparent border border-white/30 text-white placeholder:text-white/50 focus:outline-none focus:border-[#E0A67B]"
                />
                <button className="px-8 py-4 bg-white text-black font-medium hover:bg-[#E0A67B] hover:text-white transition-colors duration-500 sm:ml-0 mt-4 sm:mt-0">
                  Subscribe
                </button>
              </div>
              <p className="text-sm text-gray-400 mt-4 opacity-80">
                By subscribing, you agree to our Privacy Policy and consent to receive updates.
              </p>
            </motion.form>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <Footer />
      
      {/* Product Detail Modal */}
      <AnimatePresence>
        {activeItem && (
          <motion.div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div 
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              onClick={closeItemDetail}
            ></motion.div>
            
            <motion.div
              className="relative bg-white w-full max-w-6xl max-h-[90vh] overflow-auto z-10"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 50 }}
              transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
            >
              <button
                onClick={closeItemDetail}
                className="absolute top-8 right-8 z-20 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors duration-300"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <div className="grid grid-cols-1 md:grid-cols-2">
                <div className="h-full">
                  <div className="relative h-[60vh] md:h-full overflow-hidden">
                    <Swiper
                      modules={[EffectFade, Navigation, Autoplay]}
                      effect="fade"
                      speed={1000}
                      autoplay={{ delay: 5000, disableOnInteraction: false }}
                      loop={true}
                      navigation
                      className="h-full"
                    >
                      <SwiperSlide>
                        <img
                          src={activeItem.image}
                          alt={activeItem.name}
                          className="w-full h-full object-cover object-center"
                        />
                      </SwiperSlide>
                      <SwiperSlide>
                        <img
                          src={activeItem.secondaryImage || activeItem.image}
                          alt={activeItem.name}
                          className="w-full h-full object-cover object-center"
                        />
                      </SwiperSlide>
                    </Swiper>
                  </div>
                </div>
                
                <div className="p-10 md:p-16 flex flex-col">
                  <span className="text-[#E0A67B] uppercase tracking-[0.2em] mb-3">{activeItem.category}</span>
                  <h2 className="text-3xl md:text-4xl font-medium mb-6">{activeItem.name}</h2>
                  <span className="text-2xl mb-8">${activeItem.price.toFixed(2)}</span>
                  
                  <div className="bg-gray-50 p-6 mb-10">
                    <p className="text-gray-600 leading-relaxed">
                      {activeItem.description}
                    </p>
                  </div>
                  
                  <div className="mb-8">
                    <h3 className="text-sm uppercase tracking-wide mb-4">Color</h3>
                    <div className="flex flex-wrap gap-3">
                      {activeItem.colors?.map((color, idx) => (
                        <button 
                          key={idx} 
                          className={`w-10 h-10 rounded-full border-2 transition-all ${
                            selectedSize ? 'border-gray-300' : 'border-black'
                          } hover:scale-110`}
                          style={{ backgroundColor: color }}
                          onClick={() => handleColorSelect(color)}
                        />
                      ))}
                    </div>
                  </div>
                  
                  <div className="mb-10">
                    <h3 className="text-sm uppercase tracking-wide mb-4">Size</h3>
                    <div className="flex flex-wrap gap-3">
                      {activeItem.sizes?.map(size => (
                        <button 
                          key={size} 
                          onClick={() => handleSizeSelect(size)}
                          className={`w-14 h-14 flex items-center justify-center border transition-all ${
                            selectedSize === size 
                              ? 'border-black bg-black text-white' 
                              : 'border-gray-300 hover:border-gray-700'
                          }`}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="mt-auto space-y-4">
                    <motion.button 
                      className="w-full py-4 bg-black text-white font-medium hover:bg-[#E0A67B] transition-all duration-300"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      disabled={!selectedSize}
                    >
                      {selectedSize ? 'Add to Cart' : 'Select Size'}
                    </motion.button>
                    
                    <motion.button 
                      className="w-full py-4 border border-black text-black font-medium hover:bg-black hover:text-white transition-all duration-300"
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      Add to Wishlist
                    </motion.button>
                    
                    <div className="pt-6 border-t border-gray-100">
                      <div className="flex space-x-8 text-sm text-gray-500">
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <span>Free shipping</span>
                        </div>
                        <div className="flex items-center">
                          <svg className="w-4 h-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          <span>30-day returns</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="px-10 py-12 border-t border-gray-200">
                <div className="max-w-4xl mx-auto">
                  <h3 className="text-2xl font-medium mb-6">You May Also Like</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-6">
                    {products
                      .filter(p => p.category === activeItem.category && p.id !== activeItem.id)
                      .slice(0, 4)
                      .map((product) => (
                        <motion.div 
                          key={product.id}
                          className="cursor-pointer"
                          onClick={() => {
                            closeItemDetail();
                            setTimeout(() => openItemDetail(product), 100);
                          }}
                          whileHover={{ y: -8 }}
                        >
                          <div className="relative overflow-hidden aspect-[3/4]">
                            <img 
                              src={product.image} 
                              alt={product.name} 
                              className="w-full h-full object-cover object-center"
                            />
                          </div>
                          <h4 className="mt-3 text-sm font-medium">{product.name}</h4>
                          <p className="text-sm text-gray-600">${product.price.toFixed(2)}</p>
                        </motion.div>
                      ))
                    }
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* MobileMenu */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div 
            className="fixed inset-0 z-50 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div 
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMenuOpen(false)}
            ></motion.div>
            <motion.div 
              className="absolute right-0 top-0 bottom-0 w-full max-w-xs bg-white p-6 flex flex-col h-full"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 300 }}
            >
              <div className="flex justify-between items-center mb-8">
                <span className="text-xl font-bold">Menu</span>
                <button 
                  onClick={() => setIsMenuOpen(false)}
                  className="p-2"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="flex-1 overflow-auto">
                <nav className="flex flex-col space-y-4">
                  <a href="#" className="py-3 text-lg border-b border-gray-100">Home</a>
                  <a href="#" className="py-3 text-lg border-b border-gray-100">Collections</a>
                  <a href="#" className="py-3 text-lg border-b border-gray-100">Lookbook</a>
                  <a href="#" className="py-3 text-lg border-b border-gray-100">About</a>
                  <a href="#" className="py-3 text-lg border-b border-gray-100">Contact</a>
                </nav>
              </div>
              <div className="mt-auto pt-6 border-t border-gray-100">
                <div className="flex justify-between">
                  <a href="#" className="text-sm text-gray-600 hover:text-black">Account</a>
                  <a href="#" className="text-sm text-gray-600 hover:text-black">Login</a>
                  <a href="#" className="text-sm text-gray-600 hover:text-black">Cart (0)</a>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Catalog;